import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ParsedAddress } from '@components/common/AddressField/AddressField.component'
import { US_STATE_OPTIONS } from '@constants/Providers'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { areValuesEqual } from '@utils/settings.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { fetchClinicById, updateClinic } from '@store/actions/clinic.action'
import { IClinic } from '@dataTypes/clinic.type'
import { getClinicNumber } from '@utils/clinic.util'
import { toStateCode } from '@utils/address.util'
import { generateUploadUrl, uploadFile } from '@services/api/clinic.api'
import { updateClinicNumber } from '@services/api/phoneManagement.api'
import { BLOB_FOLDERS } from '@constants/Azure'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { toStableBlobUrl, useSignedBlobUrl } from '@hooks/useSignedBlobUrl'

const DEFAULT_CLINIC_LOGO_URL =
  'https://cdn-icons-png.flaticon.com/512/13712/13712994.png'

const LOADING_CONTAINER = 'clinic-information-settings'

const CLINIC_LOGO_FOLDER = BLOB_FOLDERS.CLINIC_LOGO

const normalizeStoredLogoUrl = (url: string | null): string | null =>
  url && /[?&]sig=/.test(url) ? toStableBlobUrl(url) : url

export interface IClinicInformationForm {
  practiceName: string
  specialty: string
  practiceNpi: string
  phone: string
  fax: string
  email: string
  website: string
  address1: string
  address2: string
  city: string
  state: string
  zip: string
  timezone: string
  logoUrl: string | null
}

const EMPTY_FORM: IClinicInformationForm = {
  practiceName: '',
  specialty: 'general',
  practiceNpi: '',
  phone: '',
  fax: '',
  email: '',
  website: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  timezone: 'America/Los_Angeles',
  logoUrl: null,
}

const formValuesForCompare = (
  form: IClinicInformationForm,
): (string | boolean | number)[] => [
  form.practiceName,
  form.specialty,
  form.practiceNpi,
  form.phone,
  form.fax,
  form.email,
  form.website,
  form.address1,
  form.address2,
  form.city,
  form.state,
  form.zip,
  form.timezone,
  form.logoUrl ?? '',
]

const clinicToForm = (clinic: IClinic): IClinicInformationForm => ({
  practiceName: clinic.clinic_name ?? '',
  specialty: clinic.specialty ?? 'general',
  practiceNpi: clinic.practice_npi ?? '',
  phone: getClinicNumber(clinic) || clinic.phone_number || '',
  fax: clinic.fax_number ?? '',
  email: clinic.email_address ?? '',
  website: clinic.website_url ?? '',
  address1: clinic.address ?? '',
  address2: clinic.address_line_2 ?? '',
  city: clinic.city ?? '',
  state: toStateCode(clinic.state) || clinic.state || '',
  zip: clinic.zip ?? '',
  timezone: clinic.time_zone || 'America/Los_Angeles',
  logoUrl: clinic.logo_url ?? null,
})

const formToClinicUpdates = (
  form: IClinicInformationForm,
): Partial<IClinic> => ({
  clinic_name: form.practiceName,
  specialty: form.specialty,
  practice_npi: form.practiceNpi,
  fax_number: form.fax,
  email_address: form.email,
  website_url: form.website.trim() || null,
  address: form.address1,
  address_line_2: form.address2.trim() || null,
  city: form.city,
  state: form.state,
  zip: form.zip,
  time_zone: form.timezone,
  logo_url: normalizeStoredLogoUrl(form.logoUrl),
})

const getActiveClinicNumberId = (clinic: IClinic): string | undefined => {
  const numbers = clinic.clinic_number ?? []
  const main = numbers.find((n) => n.status === 'Active' && n.is_main)
  if (main?.clinic_number_id) return main.clinic_number_id
  const active = numbers.find((n) => n.status === 'Active')
  return active?.clinic_number_id
}

async function uploadClinicLogo(clinicId: string, file: File): Promise<string> {
  const ext =
    (file.name.match(/\.[a-zA-Z0-9]+$/)?.[0] ?? '').toLowerCase() || '.png'
  const filename = `logo-${Date.now()}${ext.startsWith('.') ? ext : `.${ext}`}`
  const contentType = file.type || 'application/octet-stream'
  const uploadRes = await generateUploadUrl(contentType, filename, {
    folder: CLINIC_LOGO_FOLDER,
    clinic_id: clinicId,
  })
  if (uploadRes.statusCode !== 200 || !uploadRes.data?.upload_url) {
    throw new Error(uploadRes.message || 'Could not get upload URL')
  }
  await uploadFile(uploadRes.data.upload_url, file)
  return uploadRes.data.blob_url || toStableBlobUrl(uploadRes.data.upload_url)
}

export interface IClinicInformationHooks {
  data: IClinicInformationForm
  setField: <K extends keyof IClinicInformationForm>(
    key: K,
    value: IClinicInformationForm[K],
  ) => void
  applyPlaceAddress: (parsed: ParsedAddress) => void
  handleStateChange: (state: string) => void
  handleReset: () => void
  handleSave: () => Promise<void>
  hasChanges: boolean
  loading: boolean
  logoPreviewUrl: string
  handleLogoChange: (file: File) => void
  handleDeleteLogo: () => void
  setHeaderButtons: Dispatch<SetStateAction<ReactNode>>
  clinicId: string
}

const useClinicInformation = (): IClinicInformationHooks => {
  const dispatch = useAppDispatch()
  const { setHeaderButtons } = useSettingsHeader()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''

  const [savedBaseline, setSavedBaseline] =
    useState<IClinicInformationForm>(EMPTY_FORM)
  const [data, setData] = useState<IClinicInformationForm>(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null)
  const [logoObjectUrl, setLogoObjectUrl] = useState<string | null>(null)
  const logoObjectUrlRef = useRef<string | null>(null)
  const placeAddressRef = useRef<{
    addr: string
    city: string
    state: string
    zip: string
  } | null>(null)
  const activeClinicNumberIdRef = useRef<string | undefined>(undefined)
  const savedPhoneRef = useRef('')

  const savedLogoPreviewUrl = useSignedBlobUrl(
    data.logoUrl,
    DEFAULT_CLINIC_LOGO_URL,
  )
  const logoPreviewUrl = logoObjectUrl ?? savedLogoPreviewUrl

  useSettingsSubpageLoading(!dataReady)

  const hasChanges = useMemo(() => {
    if (pendingLogoFile) return true
    return !areValuesEqual(
      formValuesForCompare(data),
      formValuesForCompare(savedBaseline),
    )
  }, [data, savedBaseline, pendingLogoFile])

  useEffect(() => {
    if (!hasChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasChanges])

  const clearLogoPreview = useCallback((): void => {
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current)
      logoObjectUrlRef.current = null
    }
    setLogoObjectUrl(null)
    setPendingLogoFile(null)
  }, [])

  const loadClinic = useCallback(async (): Promise<void> => {
    if (!clinicId) {
      placeAddressRef.current = null
      setSavedBaseline(EMPTY_FORM)
      setData(EMPTY_FORM)
      clearLogoPreview()
      activeClinicNumberIdRef.current = undefined
      savedPhoneRef.current = ''
      setDataReady(true)
      return
    }

    setLoading(true)
    try {
      const clinic = await dispatch(fetchClinicById(clinicId)).unwrap()
      const mapped = clinicToForm(clinic)
      activeClinicNumberIdRef.current = getActiveClinicNumberId(clinic)
      savedPhoneRef.current = mapped.phone
      clearLogoPreview()
      placeAddressRef.current = null
      setSavedBaseline(mapped)
      setData(mapped)
    } catch {
      showToast(
        createFailedToast(
          'Clinic information',
          'Failed to load clinic information',
        ),
      )
    } finally {
      setLoading(false)
      setDataReady(true)
    }
  }, [clinicId, clearLogoPreview, dispatch, showToast])

  useEffect(() => {
    void loadClinic()
  }, [loadClinic])

  const setField = useCallback(
    <K extends keyof IClinicInformationForm>(
      key: K,
      value: IClinicInformationForm[K],
    ): void => {
      setData((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const applyPlaceAddress = useCallback((parsed: ParsedAddress): void => {
    const address1 =
      parsed.formattedAddress2?.trim() || parsed.formattedAddress?.trim() || ''
    const city = parsed.city?.trim() ?? ''
    const state = toStateCode(parsed.state) || parsed.state?.trim() || ''
    const zip = parsed.postalCode?.trim() ?? ''
    placeAddressRef.current = { addr: address1, city, state, zip }
    setData((prev) => ({
      ...prev,
      address1,
      city,
      state,
      zip: zip || prev.zip,
    }))
  }, [])

  useEffect(() => {
    const last = placeAddressRef.current
    if (!last) return
    if ((data.address1 || '') !== last.addr) return
    if (
      (data.city || '') === last.city &&
      (data.state || '') === last.state &&
      (data.zip || '') === last.zip
    ) {
      return
    }
    setData((prev) => ({
      ...prev,
      city: last.city,
      state: last.state,
      zip: last.zip || prev.zip,
    }))
  }, [data.address1, data.city, data.state, data.zip])

  const handleStateChange = useCallback((state: string): void => {
    const defaultZip =
      US_STATE_OPTIONS.find((option) => option.value === state)?.zip ?? ''
    setData((prev) => ({
      ...prev,
      state,
      zip: defaultZip,
    }))
  }, [])

  const handleReset = useCallback((): void => {
    clearLogoPreview()
    placeAddressRef.current = null
    setData({ ...savedBaseline })
  }, [clearLogoPreview, savedBaseline])

  const handleLogoChange = useCallback((file: File): void => {
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current)
    }
    const url = URL.createObjectURL(file)
    logoObjectUrlRef.current = url
    setPendingLogoFile(file)
    setLogoObjectUrl(url)
  }, [])

  const handleDeleteLogo = useCallback((): void => {
    clearLogoPreview()
    setData((prev) => ({ ...prev, logoUrl: null }))
  }, [clearLogoPreview])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    setLoading(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      let logoUrl = data.logoUrl
      if (pendingLogoFile) {
        logoUrl = await uploadClinicLogo(clinicId, pendingLogoFile)
      }

      const updates = formToClinicUpdates({ ...data, logoUrl })
      const updated = await dispatch(
        updateClinic({ clinic_id: clinicId, updates }),
      ).unwrap()

      const phoneChanged =
        data.phone.trim() !== savedPhoneRef.current.trim() &&
        data.phone.trim().length > 0
      if (phoneChanged && activeClinicNumberIdRef.current) {
        const phoneRes = await updateClinicNumber(
          activeClinicNumberIdRef.current,
          { phone_number: data.phone.trim() },
        )
        if (phoneRes.statusCode !== 200) {
          showToast(
            createFailedToast(
              'Clinic information',
              phoneRes.message || 'Clinic saved but phone number update failed',
            ),
          )
        }
      }

      clearLogoPreview()
      const mapped = clinicToForm({
        ...updated,
        phone_number: phoneChanged
          ? data.phone.trim()
          : getClinicNumber(updated),
      })
      if (phoneChanged) {
        mapped.phone = data.phone.trim()
      }
      setSavedBaseline(mapped)
      setData(mapped)
      savedPhoneRef.current = mapped.phone
      showToast(
        createSuccessToast('Saved', 'Clinic information saved successfully.'),
      )
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to save clinic information'
      showToast(createFailedToast('Clinic information', message))
    } finally {
      setLoading(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    clinicId,
    clearLogoPreview,
    data,
    dispatch,
    hideLoading,
    pendingLogoFile,
    showLoading,
    showToast,
  ])

  return {
    data,
    setField,
    applyPlaceAddress,
    handleStateChange,
    handleReset,
    handleSave,
    hasChanges,
    loading,
    logoPreviewUrl,
    handleLogoChange,
    handleDeleteLogo,
    setHeaderButtons,
    clinicId,
  }
}

export default useClinicInformation
